import { WebhookPayload } from '@ankor-io/common/types/awx'

/**
 * AWX card creation request params, @see https://www.airwallex.com/docs/api#/Issuing/Cards/_api_v1_issuing_cards_create/post
 */
export type CreateAWXCardRequest = {
  /**
   * Set this to true to activate the physical card when it is created.
   * This will enable use of the card while it’s being delivered to the cardholder.
   * Virtual cards will always be activated upon creation.
   */
  activate_on_issue?: boolean,
  authorization_controls: {
    allowed_transaction_count: 'SINGLE' | 'MULTIPLE',
    transaction_limits: {
      /**
       * Currency for transaction limits (3-letter ISO-4217 code). USD will be used if not set.
       * Examples of valid base currencies are USD, AUD, GBP, EUR, CNY, HKD, SGD, NZD, JPY, CAD, CHF.
       */
      currency?: string,
      limits: {
        amount: number,
        interval: AWXCardLimitsInterval
      }[]
    }
  },
  cardholder_id: string,
  /**
   * full legal name of user requesting the new card
   */
  created_by: string,
  form_factor: 'VIRTUAL' | 'PHYSICAL',
  is_personalized: boolean,
  program: {
    purpose: 'COMMERCIAL'
  }
  /**
   * A unique request ID specified by the client. Requests with the same request_id will be ignored.
   * This allows requests to be replayed if client is unsure of the outcome, e.g. due to network issues, system failures, etc.
   * Note: Can be non-UUID as long as it is unique between requests
   */
  request_id: string,
  nick_name?: string
}

export enum AWXCardLimitsInterval {
  PER_TRANSACTION = 'PER_TRANSACTION',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  ALL_TIME = 'ALL_TIME',
}

/**
 * AWX card response, @see https://www.airwallex.com/docs/api#/Issuing/Cards/_api_v1_issuing_cards_create/post
 * TODO: add fields as you see fit
 */
export type AWXCardResponse = {
  /**
   * Card brand
   */
  brand: string
  card_id: string
  /**
   * Masked card number
   */
  card_number: string
  card_status: AWXCardStatus
  card_version: number
  cardholder_id: string
  created_at: string
  created_by: string
  form_factor: 'VIRTUAL' | 'PHYSICAL'
  name_on_card: string
}

/**
 * For more info @see https://www.airwallex.com/docs/issuing__create-cards__card-statuses
 */
export enum AWXCardStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE', // frozen
  BLOCKED = 'BLOCKED',
  LOST = 'LOST',
  STOLEN = 'STOLEN',
  CLOSED = 'CLOSED', // cancelled
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED',
}

/**
 * Request to create a PAN token for secure card details retrieval
 * @see https://www.airwallex.com/docs/issuing__retrieve-sensitive-card-details__secure-iframes
 */
export type CreateAWXPANTokenRequest = {
  card_id: string
}

/**
 * Response from creating a PAN token
 * @see https://www.airwallex.com/docs/issuing__retrieve-sensitive-card-details__secure-iframes
 */
export type AWXPANTokenResponse = {
  /**
   * The PAN token used for secure card details retrieval
   */
  token: string
  /**
   * Token expiration timestamp (tokens expire after 1 minute)
   */
  expires_at: string
}

export type UpdateAWXCardRequest = {
  /**
   * The status to update the card to
   * INACTIVE: The card is frozen
   * CLOSED: The card is cancelled
   * ACTIVE: The card is unfrozen
   */
  card_status?: AWXCardStatus.ACTIVE | AWXCardStatus.INACTIVE | AWXCardStatus.CLOSED,
  nick_name?: string,
  authorization_controls?: {
    transaction_limits: {
      currency?: string,
      limits: {
        amount: number,
        interval: AWXCardLimitsInterval
      }[]
    },
    cash_withdrawal_limits: {
      currency?: string,
      limits: {
        amount: number,
        interval: AWXCardLimitsInterval
      }[]
    }
  }
}

export type UpdateAWXCardStatusRequest = {
  /**
   * Those are the only allowed statuses to update a card to
   * INACTIVE: The card is frozen
   * CLOSED: The card is cancelled
   * ACTIVE: The card is unfrozen
   */
  status: AWXCardStatus.ACTIVE | AWXCardStatus.INACTIVE | AWXCardStatus.CLOSED
}

export type GetCardRemainingLimitsResponse = {
  /**
   * Currency for transaction limits (3-letter ISO-4217 code). USD will be used if not set.
   * Examples of valid base currencies are USD, AUD, GBP, EUR, CNY, HKD, SGD, NZD, JPY, CAD, CHF.
   */
  currency: string
  /**
   * Cash withdrawal limits
   */
  cash_withdrawal_limits: {
    /**
     * Amount of the limit in dollars (can have decimal places)
     */
    amount: number
    /**
     * Interval for the limit
     */
    interval: AWXCardLimitsInterval
    /**
     * Remaining amount of the limit in dollars (can have decimal places)
     */
    remaining: number
  }[]
  /**
   * Transaction limits
   */
  limits: {
    /**
     * Amount of the limit in dollars (can have decimal places)
     */
    amount: number
    /**
     * Interval for the limit
     */
    interval: AWXCardLimitsInterval
    /**
     * Remaining amount of the limit in dollars (can have decimal places)
     */
    remaining: number
  }[]
}

// from https://www.airwallex.com/docs/api#/Issuing/Transactions/_api_v1_issuing_transactions__id_/get
export enum AWXCardTransactionStatus {
  APPROVED = 'APPROVED',
  CLEARED = 'CLEARED',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  REVERSED = 'REVERSED',
}

// from https://www.airwallex.com/docs/api#/Issuing/Transactions/_api_v1_issuing_transactions/get
export enum AWXCardTransactionType {
  AUTHORIZATION = 'AUTHORIZATION',
  CLEARING = 'CLEARING',
  REFUND = 'REFUND',
  REVERSAL = 'REVERSAL',
  ORIGINAL_CREDIT = 'ORIGINAL_CREDIT',
}

export type CardTransactionWebhookPayload = WebhookPayload<{
  acquiring_institution_identifier: string
  auth_code: string
  billing_amount: number
  billing_currency: string
  card_id: string
  card_nickname: string
  failure_reason: string | null
  lifecycle_id: string
  masked_card_number: string
  matched_authorizations: string[]
  merchant: {
    category_code: string
    city: string
    country: string
    identifier: string
    name: string
  }
  fee_details: {
    amount: number
    currency: string
    type: string
  }[]
  network_transaction_id: string
  posted_date: string
  retrieval_ref: string
  status: AWXCardTransactionStatus
  transaction_amount: number
  transaction_currency: string
  transaction_date: string
  transaction_id: string
  transaction_type: AWXCardTransactionType
}> & {
  account_id: string
}
